import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { isStaffUser, useAuthStore } from '../../stores/authStore'
import { ShieldX, Loader2, Lock } from 'lucide-react'

interface ProtectedRouteProps {
  children: React.ReactNode
  /** Permission(s) requise(s) pour accéder à cette route */
  permission?: string | string[]
  /** Rôle(s) requis : au moins un doit correspondre */
  roles?: string[]
  /** Rôle(s) exclu(s) : si l'utilisateur en a un, accès refusé */
  excludeRoles?: string[]
  /** Si true, toutes les permissions sont requises (AND), sinon une seule suffit (OR) */
  requireAll?: boolean
}

export default function ProtectedRoute({ 
  children, 
  permission, 
  roles,
  excludeRoles,
  requireAll = false 
}: ProtectedRouteProps) {
  const location = useLocation()
  const { 
    isAuthenticated, 
    isLoading, 
    isInitialized, 
    checkAuth,
    shouldRevalidate, 
    user,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
  } = useAuthStore()

  useEffect(() => {
    // Ne vérifier l'auth que si :
    // 1. Pas encore initialisé (premier chargement)
    // 2. Ou si le cache a expiré (revalidation nécessaire)
    if (!isInitialized || shouldRevalidate()) {
      checkAuth()
    }
  }, [isInitialized, checkAuth, shouldRevalidate])

  // Afficher un loader pendant la vérification initiale
  if (!isInitialized || isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-amber-500" />
          <p className="text-gray-600">Vérification de l'authentification...</p>
        </div>
      </div>
    )
  }

  // Rediriger vers login si non authentifié
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Vérifier que l'utilisateur fait partie du back-office (rôles admin)
  if (user && !isStaffUser(user)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <ShieldX className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Accès refusé</h2>
          <p className="mt-2 text-gray-600">Cette zone est réservée aux administrateurs.</p>
          <button
            onClick={() => useAuthStore.getState().logout()}
            className="mt-4 rounded-lg bg-gray-900 px-4 py-2 text-white hover:bg-gray-800"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    )
  }

  // Vérifier les rôles exclus si spécifiés
  if (excludeRoles && excludeRoles.length > 0 && excludeRoles.some((role) => hasRole(role))) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <ShieldX className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Accès refusé</h2>
          <p className="mt-2 text-gray-600">Votre rôle ne permet pas d'accéder à cette zone.</p>
          <button
            onClick={() => window.history.back()}
            className="mt-4 rounded-lg bg-gray-900 px-4 py-2 text-white hover:bg-gray-800"
          >
            Retour
          </button>
        </div>
      </div>
    )
  }

  // Vérifier les rôles requis si spécifiés (au moins un)
  if (roles && roles.length > 0 && !roles.some((role) => hasRole(role))) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <ShieldX className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Accès refusé</h2>
          <p className="mt-2 text-gray-600">Cette zone est réservée : {roles.join(' / ')}.</p>
          <button
            onClick={() => window.history.back()}
            className="mt-4 rounded-lg bg-gray-900 px-4 py-2 text-white hover:bg-gray-800"
          >
            Retour
          </button>
        </div>
      </div>
    )
  }

  // Vérifier les permissions si spécifiées. Le superadmin est soumis aux
  // mêmes vérifications : son rôle étant confiné (seeder backend), la liste
  // explicite renvoyée par /me/permissions fait foi pour lui aussi.
  if (permission) {
    const permissions = Array.isArray(permission) ? permission : [permission]
    const hasAccess = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions)

    if (!hasAccess) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
          <div className="mx-auto max-w-md text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
              <Lock className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Permission insuffisante</h2>
            <p className="mt-2 text-gray-600">
              Vous n'avez pas les droits nécessaires pour accéder à cette page.
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {requireAll ? 'Permissions requises: ' : 'Une de ces permissions est requise: '}
              <span className="font-medium">{permissions.join(', ')}</span>
            </p>
            <button
              onClick={() => window.history.back()}
              className="mt-6 rounded-lg bg-amber-500 px-6 py-2 text-white hover:bg-amber-600"
            >
              Retour
            </button>
          </div>
        </div>
      )
    }
  }

  return <>{children}</>
}

/**
 * Hook pour vérifier une permission dans un composant
 */
export function usePermission(permission: string | string[], requireAll = false): boolean {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = useAuthStore()
  
  const permissions = Array.isArray(permission) ? permission : [permission]
  return requireAll 
    ? hasAllPermissions(permissions)
    : permissions.length === 1 
      ? hasPermission(permissions[0])
      : hasAnyPermission(permissions)
}

/**
 * Composant pour afficher conditionnellement du contenu basé sur les permissions
 */
interface CanProps {
  permission: string | string[]
  requireAll?: boolean
  children: React.ReactNode
  fallback?: React.ReactNode
}

export function Can({ permission, requireAll = false, children, fallback = null }: CanProps) {
  const hasAccess = usePermission(permission, requireAll)
  return hasAccess ? <>{children}</> : <>{fallback}</>
}
